import React, { useState, useEffect } from "react";
import {
  Modal,
  Form,
  Button,
  Alert,
  Spinner,
  ProgressBar,
  ButtonGroup,
  InputGroup,
} from "react-bootstrap";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";
import { Icon } from "../../../utils/iconHelper";
import StudyResourceService, { StudyResource, Test } from "../../../_services/StudyResourceService";
import RubricService, { Rubric } from "../../../_services/RubricService";
import ApiService from "../../../_services/ApiService";
import TestBrowserModal from "../../../components/TestBrowserModal";
import useGoogleDrivePicker from "../../../hooks/useGoogleDrivePicker";
import { ResourceType, ResourceSubType } from "../../../components/LMBuilder/types";
import { imageUploadPlugin } from "../../../_services/ckeditorImageUploadAdapter";
// [discussion-board type=20] role list reused from the legacy create flow
import DiscussionBoardService, { RoleBean } from "../../../_services/DiscussionBoardService";
import { STUDY_RESOURCE_TYPE, SCORM_COURSE_TYPE_ID } from "../../../_constants/studyResourceType";
import SpinWheelEditor from "./SpinWheelEditor";
import DecisionResourceStudio from "./DecisionResourceStudio";

interface Props {
  show: boolean;
  onHide: () => void;
  resource: StudyResource | null;
  onSuccess: () => void;
}

const StudyResourceFormModal: React.FC<Props> = ({ show, onHide, resource, onSuccess }) => {
  const [formData, setFormData] = useState<StudyResource>({
    name: "",
    typeId: STUDY_RESOURCE_TYPE.DOWNLOAD,
    url: "",
    content: "",
  });

  const [markAsImportant, setMarkAsImportant] = useState(false);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Vimeo upload states
  const [vimeoInputMethod, setVimeoInputMethod] = useState<"url" | "upload" | "gdrive">("url");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);

  // Google Drive picker
  const { openPicker: openGDrivePicker, loading: gdrivePickerLoading } = useGoogleDrivePicker();
  const [gdriveUploading, setGdriveUploading] = useState(false);

  // Test selection states
  const [showTestBrowser, setShowTestBrowser] = useState(false);
  const [selectedTest, setSelectedTest] = useState<Test | null>(null);

  // Rubric selection states
  const [rubrics, setRubrics] = useState<Rubric[]>([]);
  const [rubricsLoading, setRubricsLoading] = useState(false);

  // Resource types from API
  const [resourceTypes, setResourceTypes] = useState<ResourceType[]>([]);
  const [resourceTypesLoading, setResourceTypesLoading] = useState(false);

  // Store signed URL temporarily for download
  const [uploadedSignedUrl, setUploadedSignedUrl] = useState<string | null>(null);

  // Custom Pathway: loading flag for the "Suggest guardrails" Claude call

  // [discussion-board type=20] board fields — saved in study_discussion_board, linked by Id
  const [dbDescription, setDbDescription] = useState("");
  const [dbActive, setDbActive] = useState(true);
  const [dbAcceptStudentPosts, setDbAcceptStudentPosts] = useState(true);
  const [dbCommentRoleIds, setDbCommentRoleIds] = useState<number[]>([]);
  const [dbSendToEmails, setDbSendToEmails] = useState("");
  // [discussion-board type=20] notification toggle — stored in study_resource_master.Config.sendEmails
  const [dbSendEmails, setDbSendEmails] = useState(false);
  const [dbRoles, setDbRoles] = useState<RoleBean[]>([]);
  const [dbRolesLoading, setDbRolesLoading] = useState(false);

  useEffect(() => {
    if (resource) {
      console.log('📝 Loading resource:', resource);
      console.log('📝 Resource config:', resource.config);
      console.log('📝 Resource metaData:', resource.metaData);

      setFormData({
        name: resource.name,
        typeId: resource.typeId,
        url: resource.url || "",
        content: resource.content || "",
        config: resource.config,
        subTypeId: resource.subTypeId,
        instructorId: resource.instructorId,
        rubricId: resource.rubricId,
        metaData: resource.metaData,
      });

      // Parse MetaData and check for markItImportant
      let isImportant = false;
      if (resource.metaData) {
        try {
          const metaDataObj = typeof resource.metaData === 'string'
            ? JSON.parse(resource.metaData)
            : resource.metaData;
          console.log('📋 Parsed metaData object:', metaDataObj);
          console.log('📋 markItImportant value:', metaDataObj?.markItImportant);
          isImportant = metaDataObj?.markItImportant === 1;
          console.log('📋 isImportant result:', isImportant);
        } catch (e) {
          console.warn('Failed to parse metaData:', e);
        }
      }
      setMarkAsImportant(isImportant);

      // If editing Type 36 resource, load the selected test
      if (resource.typeId === STUDY_RESOURCE_TYPE.ONLINE_TEST && resource.config?.testId) {
        console.log('🔍 Fetching test details for testId:', resource.config.testId);
        fetchTestDetails(resource.config.testId);
      } else {
        console.log('🔄 Not Type 36 or no testId, resetting selectedTest');
        // Only reset selectedTest if not Type 36
        setSelectedTest(null);
      }

      // [discussion-board type=20] prefill board fields from joined study_discussion_board.
      // The icon URL lives in formData.url (the regular field). Older boards may only have
      // the icon stored, so fall back to it when study_resource_master.URL is empty.
      if (resource.typeId === STUDY_RESOURCE_TYPE.DISCUSSION_BOARD) {
        setDbDescription(resource.description ?? "");
        setDbActive(resource.active !== false);
        setDbAcceptStudentPosts(resource.acceptStudentPosts !== false);
        setDbSendToEmails(resource.sendToEmails ?? "");
        // Prefill from Config.sendEmails (the source of truth). The list endpoint
        // returns `config` as a parsed object; GET-by-id additionally surfaces
        // `sendEmails` at top level — handle both.
        {
          const cfg = typeof resource.config === "string"
            ? (() => { try { return JSON.parse(resource.config as unknown as string); } catch { return {}; } })()
            : (resource.config as any) ?? {};
          setDbSendEmails(resource.sendEmails === true || cfg?.sendEmails === true);
        }
        setDbCommentRoleIds(
          resource.acceptCommentFromRoleIds
            ? resource.acceptCommentFromRoleIds.split(",").map((s) => Number(s.trim())).filter((n) => !isNaN(n))
            : []
        );
        if (!resource.url && resource.icon) {
          setFormData((prev) => ({ ...prev, url: resource.icon || "" }));
        }
      }
    } else {
      console.log('➕ Creating new resource');
      setFormData({
        name: "",
        typeId: STUDY_RESOURCE_TYPE.DOWNLOAD,
        url: "",
        content: "",
      });
      setSelectedTest(null);
      setMarkAsImportant(false);
      // [discussion-board type=20] reset board fields for a fresh create
      setDbDescription("");
      setDbActive(true);
      setDbAcceptStudentPosts(true);
      setDbCommentRoleIds([]);
      setDbSendToEmails("");
      setDbSendEmails(false);
    }
    setError(null);
    setSelectedFile(null);
    setUploadProgress(0);
    setVimeoInputMethod("url");
    setUploadedSignedUrl(null);
  }, [resource, show]);

  // Fetch rubrics and resource types when modal opens
  useEffect(() => {
    if (show) {
      fetchRubrics();
      fetchResourceTypes();
    }
  }, [show]);

  // [discussion-board type=20] load roles only when the user lands on the Discussion Board type
  useEffect(() => {
    if (!show || formData.typeId !== STUDY_RESOURCE_TYPE.DISCUSSION_BOARD || dbRoles.length > 0) return;
    setDbRolesLoading(true);
    DiscussionBoardService.listRoles()
      .then((res) => setDbRoles(res.data?.data || []))
      .catch(() => setDbRoles([]))
      .finally(() => setDbRolesLoading(false));
  }, [show, formData.typeId, dbRoles.length]);

  const fetchResourceTypes = async () => {
    setResourceTypesLoading(true);
    try {
      const response = await ApiService.getLearningResourceType();
      // Master table returns Id order — present alphabetically
      setResourceTypes(
        [...(response.data || [])].sort((a: ResourceType, b: ResourceType) =>
          (a.name || "").localeCompare(b.name || "")
        )
      );
    } catch (err) {
      console.error('Failed to fetch resource types:', err);
    } finally {
      setResourceTypesLoading(false);
    }
  };

  const fetchRubrics = async () => {
    setRubricsLoading(true);
    try {
      const response = await RubricService.getAllRubrics({ limit: 100 });
      if (response.success) {
        setRubrics(response.data.rubrics);
      }
    } catch (err) {
      console.error('Failed to fetch rubrics:', err);
    } finally {
      setRubricsLoading(false);
    }
  };

  const fetchTestDetails = async (testId: number) => {
    try {
      console.log('🔄 fetchTestDetails called for testId:', testId);
      const tests = await StudyResourceService.getAvailableTests();
      console.log('📊 Total tests received:', tests.length);
      console.log('📊 All test IDs:', tests.map(t => t.id));

      const test = tests.find(t => t.id === testId);
      console.log('🎯 Found test:', test);

      if (test) {
        console.log('✅ Setting selected test:', test.name);
        setSelectedTest(test);
      } else {
        console.warn('⚠️ Test with ID', testId, 'not found in API response');
        // Set a placeholder test object with just the ID so user knows what test is selected
        setSelectedTest({
          id: testId,
          name: `Test #${testId} (Name unavailable - test may not be in instructor's list)`,
          testName: `Test #${testId}`,
        });
      }
    } catch (error) {
      console.error('❌ Failed to fetch test details:', error);
      // Still set placeholder so user can see the testId
      setSelectedTest({
        id: testId,
        name: `Test #${testId} (Error loading test details)`,
        testName: `Test #${testId}`,
      });
    }
  };

  const handleChange = (field: keyof StudyResource, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setError(null);
  };

  const handleTypeChange = (typeId: number) => {
    setFormData((prev) => ({
      ...prev,
      typeId,
      subTypeId: null, // Reset subtype when type changes
      url: typeId === STUDY_RESOURCE_TYPE.CODE_EDITOR ? "judge0://code-editor" : "",
      content: "",
    }));
    setSelectedFile(null);
    setError(null);
  };

  // Get subtypes for the currently selected type
  const getSubtypesForSelectedType = (): ResourceSubType[] => {
    const selectedType = resourceTypes.find(t => t.id === formData.typeId);
    return selectedType?.subtypes?.filter(st => st.enableStatus === 1) || [];
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate based on resource type
      if (formData.typeId === STUDY_RESOURCE_TYPE.VIMEO) {
        // Vimeo video - validate video types
        const validTypes = ["video/mp4", "video/quicktime", "video/x-msvideo", "video/webm"];
        if (!validTypes.includes(file.type)) {
          setError("Please select a valid video file (MP4, MOV, AVI, WebM)");
          return;
        }
        // Validate file size (max 500MB for videos)
        if (file.size > 500 * 1024 * 1024) {
          setError("File size must be less than 500MB");
          return;
        }
      } else if (formData.typeId === STUDY_RESOURCE_TYPE.DISCUSSION_BOARD) {
        // [discussion-board type=20] icon — image only, small cap; uploaded URL is saved
        // to BOTH study_resource_master.URL and study_discussion_board.Icon
        if (!file.type.startsWith("image/")) {
          setError("Please select an image file for the icon");
          return;
        }
        if (file.size > 5 * 1024 * 1024) {
          setError("Icon image must be less than 5MB");
          return;
        }
      } else if (formData.typeId === STUDY_RESOURCE_TYPE.DOWNLOAD || formData.typeId === STUDY_RESOURCE_TYPE.ARTICLE || formData.typeId === STUDY_RESOURCE_TYPE.EBOOK) {
        // Download resource, Article, or E-book - accept common document types
        // No strict validation - accept any file type for flexibility
        // Validate file size (max 100MB for documents)
        if (file.size > 100 * 1024 * 1024) {
          setError("File size must be less than 100MB");
          return;
        }
      }

      setSelectedFile(file);
      setError(null);
    }
  };

  const handleFileUpload = async (): Promise<any | null> => {
    if (!selectedFile) {
      setError("Please select a file");
      return null;
    }

    setIsUploading(true);
    setUploadProgress(0);
    setError(null);

    try {
      let response;

      if (formData.typeId === STUDY_RESOURCE_TYPE.VIMEO) {
        // Vimeo video upload
        response = await StudyResourceService.uploadVideoToVimeo(
          selectedFile,
          (progress) => setUploadProgress(progress)
        );
      } else if (
        formData.typeId === STUDY_RESOURCE_TYPE.DOWNLOAD ||
        formData.typeId === STUDY_RESOURCE_TYPE.ARTICLE ||
        formData.typeId === STUDY_RESOURCE_TYPE.EBOOK ||
        formData.typeId === STUDY_RESOURCE_TYPE.DISCUSSION_BOARD // [discussion-board type=20] icon image → S3
      ) {
        // S3 file upload for Download resources, Articles, E-books, and Discussion Board icons
        response = await StudyResourceService.uploadFileToS3(
          selectedFile,
          (progress) => setUploadProgress(progress)
        );
      }

      // Update form with HTTPS URL from S3
      if (response) {
        const data: any = response.data;
        setFormData((prev) => ({
          ...prev,
          url: data.url,
          fileSizeInByte: data.fileSizeInBytes || selectedFile.size,
          ...(formData.typeId === STUDY_RESOURCE_TYPE.VIMEO && data.videoId && {
            vimeoVideoId: data.videoId,
            vimeoVideoUri: data.videoUri,
            vimeoVideoMetaData: data.videoMetaData,
            duration: data.duration,
          }),
          ...(data.pdfPageCount && {
            metaData: {
              ...(prev.metaData || {}),
              totalPages: data.pdfPageCount,
            }
          }),
        }));
        setUploadedSignedUrl(data.signedUrl || null); // Store signed URL for download
        setVimeoInputMethod("url");
        setSelectedFile(null);
        setUploadProgress(0);
        return data;
      }
      return null;
    } catch (err: any) {
      const uploadType = formData.typeId === STUDY_RESOURCE_TYPE.VIMEO ? "Vimeo" : "S3";
      setError(err.message || `Failed to upload file to ${uploadType}`);
      return null;
    } finally {
      setIsUploading(false);
    }
  };

  const handleDownloadFile = async (url: string) => {
    try {
      // If we have a signed URL from recent upload, use it
      if (uploadedSignedUrl && formData.url === url) {
        window.open(uploadedSignedUrl, '_blank');
      } else {
        // For other URLs (external or S3), open directly
        window.open(url, '_blank');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to download file');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Auto-upload the file if the user picked "Upload File" but never clicked the
    // upload button — Vimeo (and other file-upload types) still need the resulting
    // URL on formData for validation and persistence.
    let effectiveFormData = formData;
    const isFileUploadType =
      formData.typeId === STUDY_RESOURCE_TYPE.VIMEO ||
      formData.typeId === STUDY_RESOURCE_TYPE.DOWNLOAD ||
      formData.typeId === STUDY_RESOURCE_TYPE.ARTICLE ||
      formData.typeId === STUDY_RESOURCE_TYPE.EBOOK ||
      formData.typeId === STUDY_RESOURCE_TYPE.DISCUSSION_BOARD;
    if (
      isFileUploadType &&
      vimeoInputMethod === "upload" &&
      selectedFile &&
      !formData.url
    ) {
      const uploadData = await handleFileUpload();
      if (!uploadData) {
        // handleFileUpload already set the error message
        return;
      }
      effectiveFormData = {
        ...formData,
        url: uploadData.url,
        fileSizeInByte: uploadData.fileSizeInBytes || selectedFile.size,
        ...(formData.typeId === STUDY_RESOURCE_TYPE.VIMEO && uploadData.videoId && {
          vimeoVideoId: uploadData.videoId,
          vimeoVideoUri: uploadData.videoUri,
          vimeoVideoMetaData: uploadData.videoMetaData,
          duration: uploadData.duration,
        }),
      };
    }

    setLoading(true);

    // Validate
    const validationError = StudyResourceService.validateResource(effectiveFormData);
    if (validationError) {
      setError(validationError);
      setLoading(false);
      return;
    }

    try {
      // Prepare final data for submission
      const dataToSubmit: any = {
        ...effectiveFormData,
        markAsImportant // Send the boolean flag to backend
      };

      // [discussion-board type=20] include board fields in the payload (saved in study_discussion_board)
      if (effectiveFormData.typeId === STUDY_RESOURCE_TYPE.DISCUSSION_BOARD) {
        dataToSubmit.description = dbDescription.trim() || null;
        dataToSubmit.active = dbActive;
        dataToSubmit.acceptStudentPosts = dbAcceptStudentPosts;
        dataToSubmit.acceptCommentFromRoleIds = dbCommentRoleIds.length > 0 ? dbCommentRoleIds.join(",") : null;
        // [discussion-board type=20] same URL saved to both study_resource_master.URL (via formData.url)
        // and study_discussion_board.Icon
        dataToSubmit.icon = effectiveFormData.url?.trim() || null;
        dataToSubmit.sendToEmails = dbSendToEmails.trim() || null;
        // Lands in study_resource_master.Config.sendEmails — gates the topic/comment/reply email fan-out.
        dataToSubmit.sendEmails = dbSendEmails;
      }

      if (resource?.id) {
        // Update
        await StudyResourceService.updateStudyResource(resource.id, dataToSubmit);
      } else {
        // Create
        await StudyResourceService.createStudyResource(dataToSubmit);
      }
      onSuccess();
    } catch (err: any) {
      setError(err.message || `Failed to ${resource ? "update" : "create"} resource`);
    } finally {
      setLoading(false);
    }
  };

  const renderDownloadInput = () => {
    // [discussion-board type=20] reused here for the board Icon (optional); the uploaded/typed
    // URL lands in formData.url and is mirrored to study_discussion_board.Icon on submit.
    const label = formData.typeId === STUDY_RESOURCE_TYPE.ARTICLE
      ? 'Article Resource'
      : formData.typeId === STUDY_RESOURCE_TYPE.DISCUSSION_BOARD
      ? 'Icon'
      : 'Download Resource';
    const isRequired = formData.typeId !== STUDY_RESOURCE_TYPE.ARTICLE && formData.typeId !== STUDY_RESOURCE_TYPE.DISCUSSION_BOARD;
    return (
      <div className="mb-3">
        <Form.Label>{label} {isRequired && <span className="text-danger">*</span>}</Form.Label>

        <ButtonGroup className="w-100 mb-3">
          <Button
            variant={vimeoInputMethod === "url" ? "primary" : "secondary"}
            onClick={() => setVimeoInputMethod("url")}
            style={{ flex: 1 }}
          >
            Provide URL
          </Button>
          <Button
            variant={vimeoInputMethod === "upload" ? "primary" : "secondary"}
            onClick={() => setVimeoInputMethod("upload")}
            style={{ flex: 1 }}
          >
            Upload File
          </Button>
          <Button
            variant={vimeoInputMethod === "gdrive" ? "primary" : "secondary"}
            onClick={() => setVimeoInputMethod("gdrive")}
            style={{ flex: 1 }}
          >
            Google Drive
          </Button>
        </ButtonGroup>

        {vimeoInputMethod === "url" && (
          <>
            <Form.Control
              type="url"
              placeholder="https://example.com/document.pdf"
              value={formData.url || ""}
              onChange={(e) => handleChange("url", e.target.value)}
              disabled={isUploading}
            />
            {formData.url && (
              <Alert variant="success" className="mt-2 mb-0 d-flex align-items-center justify-content-between">
                <div>
                  <strong>Current File:</strong>{" "}
                  <span className="text-break">
                    <Icon.FileText size={14} className="me-1" />
                    {StudyResourceService.extractFilename(formData.url)}
                  </span>
                </div>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => handleDownloadFile(formData.url!)}
                  className="ms-2"
                >
                  <Icon.Download size={14} className="me-1" />
                  Download
                </Button>
              </Alert>
            )}
            <Form.Text className="text-muted">
              Enter the URL of the downloadable file
            </Form.Text>
          </>
        )}

        {vimeoInputMethod === "upload" && (
          <div>
            <Form.Control
              type="file"
              onChange={handleFileSelect}
              disabled={isUploading}
              className="mb-2"
            />

            {selectedFile && (
              <Alert variant="info" className="mb-2">
                <strong>Selected:</strong> {selectedFile.name} ({(selectedFile.size / (1024 * 1024)).toFixed(2)} MB)
              </Alert>
            )}

            {isUploading && (
              <div className="mb-2">
                <ProgressBar now={uploadProgress} label={`${uploadProgress}%`} animated />
                <Form.Text className="text-muted">
                  Uploading file... Please wait.
                </Form.Text>
              </div>
            )}

            {formData.url && !isUploading && (
              <Alert variant="success" className="mb-3 d-flex align-items-center justify-content-between">
                <div>
                  <strong>Upload successful!</strong>
                  <br />
                  <span className="text-break">
                    <Icon.FileText size={14} className="me-1" />
                    {StudyResourceService.extractFilename(formData.url)}
                  </span>
                </div>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => handleDownloadFile(formData.url!)}
                  className="ms-2"
                >
                  <Icon.Download size={14} className="me-1" />
                  Download
                </Button>
              </Alert>
            )}

            <Button
              type="button"
              variant="primary"
              onClick={handleFileUpload}
              disabled={!selectedFile || isUploading}
              className="w-100 mb-2"
            >
              {isUploading ? (
                <>
                  <Spinner role="status" animation="border" size="sm" className="me-2" />
                  Uploading...
                </>
              ) : (
                <>
                  <Icon.Upload className="me-2" />
                  Upload File
                </>
              )}
            </Button>

            <Form.Text className="text-muted d-block">
              Upload your file (PDF, DOC, ZIP, etc.)
            </Form.Text>
          </div>
        )}

        {vimeoInputMethod === "gdrive" && (
          <div>
            <Button
              type="button"
              variant="primary"
              className="w-100 mb-2 d-flex align-items-center justify-content-center gap-2"
              onClick={() => {
                openGDrivePicker(async (result) => {
                  setGdriveUploading(true);
                  setIsUploading(true);
                  setError(null);
                  try {
                    const response = await StudyResourceService.uploadFromGoogleDrive(
                      result.fileId, result.fileName, result.accessToken
                    );
                    if (response.success) {
                      setFormData((prev) => ({ ...prev, url: response.data.url }));
                      setVimeoInputMethod("url");
                    }
                  } catch (err: any) {
                    setError(err?.response?.data?.message || "Failed to upload from Google Drive");
                  } finally {
                    setGdriveUploading(false);
                    setIsUploading(false);
                  }
                });
              }}
              disabled={gdrivePickerLoading || gdriveUploading}
            >
              <svg width="18" height="18" viewBox="0 0 87.3 78" xmlns="http://www.w3.org/2000/svg">
                <path d="M6.6 66.85l3.85 6.65c.8 1.4 1.95 2.5 3.3 3.3l13.75-23.8H0c0 1.55.4 3.1 1.2 4.5l5.4 9.35z" fill="#0066DA"/>
                <path d="M43.65 25L29.9 1.2C28.55 2 27.4 3.1 26.6 4.5L3.55 44.4c-.8 1.4-1.2 2.95-1.2 4.5h27.5L43.65 25z" fill="#00AC47"/>
                <path d="M73.55 76.8c1.35-.8 2.5-1.9 3.3-3.3l1.6-2.75 7.65-13.25c.8-1.4 1.2-2.95 1.2-4.5H59.85l6.15 11.65 7.55 12.15z" fill="#EA4335"/>
                <path d="M43.65 25L57.4 1.2C56.05.4 54.5 0 52.85 0H34.45c-1.65 0-3.2.45-4.55 1.2L43.65 25z" fill="#00832D"/>
                <path d="M59.85 49H27.5l-13.75 23.8c1.35.8 2.9 1.2 4.55 1.2h50.4c1.65 0 3.2-.45 4.55-1.2L59.85 49z" fill="#2684FC"/>
                <path d="M73.4 26.5l-12.7-22c-.8-1.4-1.95-2.5-3.3-3.3L43.65 25l16.2 24h27.45c0-1.55-.4-3.1-1.2-4.5l-12.7-18z" fill="#FFBA00"/>
              </svg>
              {gdriveUploading ? 'Uploading to S3...' : gdrivePickerLoading ? 'Loading...' : 'Pick from Google Drive'}
            </Button>

            {gdriveUploading && (
              <div className="mb-2">
                <ProgressBar animated now={100} label="Transferring..." />
                <Form.Text className="text-muted">
                  Downloading from Drive and uploading to storage...
                </Form.Text>
              </div>
            )}

            {formData.url && !gdriveUploading && (
              <Alert variant="success" className="mb-2 d-flex align-items-center justify-content-between">
                <div>
                  <strong>File ready:</strong>{" "}
                  <span className="text-break">
                    <Icon.FileText size={14} className="me-1" />
                    {StudyResourceService.extractFilename(formData.url)}
                  </span>
                </div>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => handleDownloadFile(formData.url!)}
                  className="ms-2"
                >
                  <Icon.Download size={14} className="me-1" />
                  Download
                </Button>
              </Alert>
            )}

            <Form.Text className="text-muted d-block">
              Select a PDF from your Google Drive. The file will be copied to our storage.
            </Form.Text>
          </div>
        )}

        {formData.typeId === STUDY_RESOURCE_TYPE.ARTICLE && (
          <Form.Text className="text-muted d-block mt-2">
            <strong>Note:</strong> Upload is optional for Articles. You can provide a file/URL as a supplementary resource, but the Content (HTML) field below is required.
          </Form.Text>
        )}
      </div>
    );
  };

  // [discussion-board type=20] board-specific fields — saved in study_discussion_board, joined on Id
  const renderDiscussionBoardInput = () => {
    const toggleRole = (roleId: number) => {
      setDbCommentRoleIds((prev) =>
        prev.includes(roleId) ? prev.filter((r) => r !== roleId) : [...prev, roleId],
      );
    };

    return (
      <div className="mb-3">
        <Form.Group className="mb-3">
          <Form.Label>Description</Form.Label>
          <Form.Control
            as="textarea"
            rows={3}
            value={dbDescription}
            onChange={(e) => setDbDescription(e.target.value)}
            placeholder="What is this board for?"
          />
        </Form.Group>
        <Form.Group className="mb-3">
          <Form.Check
            type="checkbox"
            id="db-active"
            label="Active"
            checked={dbActive}
            onChange={(e) => setDbActive(e.target.checked)}
          />
        </Form.Group>
        <Form.Group className="mb-3">
          <Form.Check
            type="checkbox"
            id="db-accept-student-posts"
            label="Allow students to post new topics"
            checked={dbAcceptStudentPosts}
            onChange={(e) => setDbAcceptStudentPosts(e.target.checked)}
          />
        </Form.Group>
        <Form.Group className="mb-3">
          <Form.Label>
            Accept Comments from Role(s) <small className="text-muted">(optional)</small>
          </Form.Label>
          {dbRolesLoading ? (
            <div className="text-muted small">Loading roles…</div>
          ) : dbRoles.length === 0 ? (
            <div className="text-muted small">No roles available.</div>
          ) : (
            <div
              style={{
                maxHeight: 160,
                overflowY: "auto",
                border: "1px solid var(--border-default)",
                borderRadius: 4,
                padding: 8,
              }}
            >
              {dbRoles.map((r) => (
                <Form.Check
                  key={r.roleId}
                  type="checkbox"
                  id={`db-role-${r.roleId}`}
                  label={r.roleName}
                  checked={dbCommentRoleIds.includes(r.roleId)}
                  onChange={() => toggleRole(r.roleId)}
                />
              ))}
            </div>
          )}
          <Form.Text className="text-muted">
            Members with any of the selected roles can comment on topics in this board.
          </Form.Text>
        </Form.Group>
        {/* [discussion-board type=20] Icon reuses the regular upload/URL field (formData.url);
            the same URL is mirrored to study_discussion_board.Icon on submit. */}
        {renderDownloadInput()}
        <Form.Group className="mb-3">
          <Form.Check
            type="checkbox"
            id="db-send-emails"
            label="Send email notifications for new topics, comments, and replies"
            checked={dbSendEmails}
            onChange={(e) => setDbSendEmails(e.target.checked)}
          />
          <Form.Text className="text-muted">
            When off, no email is sent for any board activity. When on, recipients are determined by the address list below plus the staff issued to the board.
          </Form.Text>
        </Form.Group>
        <Form.Group className="mb-0">
          <Form.Label>
            Notify emails <small className="text-muted">(comma-separated, optional)</small>
          </Form.Label>
          <Form.Control
            type="text"
            value={dbSendToEmails}
            onChange={(e) => setDbSendToEmails(e.target.value)}
            placeholder="moderator@example.com, admin@example.com"
            disabled={!dbSendEmails}
          />
          <Form.Text className="text-muted">
            These addresses are notified of new topics, comments, and replies (alongside staff already issued to the board). Requires the checkbox above.
          </Form.Text>
        </Form.Group>
      </div>
    );
  };

  const renderUrlInput = () => {
    const placeholder = formData.typeId === STUDY_RESOURCE_TYPE.YOUTUBE
      ? "https://www.youtube.com/watch?v=..."
      : formData.typeId === STUDY_RESOURCE_TYPE.VIMEO
      ? "https://vimeo.com/..."
      : "https://example.com";

    return (
      <Form.Group className="mb-3">
        <Form.Label>URL <span className="text-danger">*</span></Form.Label>
        <Form.Control
          type="url"
          placeholder={placeholder}
          value={formData.url || ""}
          onChange={(e) => handleChange("url", e.target.value)}
          required
        />
        <Form.Text className="text-muted">
          Enter the full URL of the resource
        </Form.Text>
      </Form.Group>
    );
  };

  const renderVimeoInput = () => {
    return (
      <div className="mb-3">
        <Form.Label>Vimeo Video <span className="text-danger">*</span></Form.Label>

        <ButtonGroup className="w-100 mb-3">
          <Button
            variant={vimeoInputMethod === "url" ? "primary" : "secondary"}
            onClick={() => setVimeoInputMethod("url")}
            style={{ flex: 1 }}
          >
            Provide URL
          </Button>
          <Button
            variant={vimeoInputMethod === "upload" ? "primary" : "secondary"}
            onClick={() => setVimeoInputMethod("upload")}
            style={{ flex: 1 }}
          >
            Upload File
          </Button>
        </ButtonGroup>

        {vimeoInputMethod === "url" && (
          <>
            <Form.Control
              type="url"
              placeholder="https://vimeo.com/..."
              value={formData.url || ""}
              onChange={(e) => handleChange("url", e.target.value)}
              disabled={isUploading}
            />
            {formData.url && (
              <Alert variant="success" className="mt-2 mb-0">
                <strong>Current URL:</strong>{" "}
                <a href={formData.url} target="_blank" rel="noopener noreferrer" className="alert-link">
                  {formData.url}
                </a>
              </Alert>
            )}
            <Form.Text className="text-muted">
              Enter the Vimeo video URL
            </Form.Text>
          </>
        )}

        {vimeoInputMethod === "upload" && (
          <div>
            <Form.Control
              type="file"
              accept="video/*"
              onChange={handleFileSelect}
              disabled={isUploading}
              className="mb-2"
            />

            {selectedFile && (
              <Alert variant="info" className="mb-2">
                <strong>Selected:</strong> {selectedFile.name} ({(selectedFile.size / (1024 * 1024)).toFixed(2)} MB)
              </Alert>
            )}

            {isUploading && (
              <div className="mb-2">
                <ProgressBar now={uploadProgress} label={`${uploadProgress}%`} animated />
                <Form.Text className="text-muted">
                  Uploading to Vimeo... Please wait.
                </Form.Text>
              </div>
            )}

            {formData.url && !isUploading && (
              <Alert variant="success" className="mb-3">
                <strong>Upload successful!</strong>
                <br />
                <a href={formData.url} target="_blank" rel="noopener noreferrer" className="alert-link">
                  View Video on Vimeo
                </a>
              </Alert>
            )}

            <Button
              type="button"
              variant="primary"
              onClick={handleFileUpload}
              disabled={!selectedFile || isUploading}
              className="w-100 mb-2"
            >
              {isUploading ? (
                <>
                  <Spinner role="status" animation="border" size="sm" className="me-2" />
                  Uploading...
                </>
              ) : (
                <>
                  <Icon.Upload className="me-2" />
                  Upload to Vimeo
                </>
              )}
            </Button>

            <Form.Text className="text-muted d-block">
              Upload your video file to Vimeo (max 500MB)
            </Form.Text>
          </div>
        )}
      </div>
    );
  };

  const renderTestInput = () => {
    return (
      <div className="mb-3">
        <Form.Label>Select Test <span className="text-danger">*</span></Form.Label>

        {/* Display selected test */}
        <div className="selected-test-display mb-3">
          {selectedTest ? (
            <Alert
              variant="info"
              className="d-flex justify-content-between align-items-start mb-0 p-3"
              style={{ backgroundColor: 'var(--status-info-bg)', borderColor: '#bee5eb' }}
            >
              <div className="d-flex align-items-start flex-grow-1">
                <Icon.CheckCircle className="me-2 mt-1" style={{ minWidth: '20px' }} />
                <div className="flex-grow-1">
                  <div className="fw-bold mb-1" style={{ fontSize: '1.1rem' }}>
                    {selectedTest.name}
                  </div>
                  {selectedTest.categoryName && (
                    <span className="me-2" style={{ display: 'inline-block', padding: '2px 8px', fontSize: '12px', borderRadius: '4px', backgroundColor: '#6c757d', color: 'var(--text-on-brand)' }}>{selectedTest.categoryName}</span>
                  )}
                  {selectedTest.id && (
                    <small className="text-muted">ID: {selectedTest.id}</small>
                  )}
                </div>
              </div>
              <Button
                variant="danger"
                size="sm"
                onClick={() => {
                  setSelectedTest(null);
                  handleChange("config", {
                    ...formData.config,
                    testId: undefined
                  });
                }}
                className="ms-3"
                style={{ minWidth: '80px' }}
              >
                <Icon.X className="me-1" /> Clear
              </Button>
            </Alert>
          ) : (
            <Alert variant="warning" className="mb-0 p-3">
              <Icon.Inbox className="me-2" />
              No test selected - click "Browse Tests" to select a test
            </Alert>
          )}
        </div>

        {/* Browse tests button */}
        <div className="d-grid mb-4">
          <Button
            variant="primary"
            size="lg"
            onClick={() => setShowTestBrowser(true)}
          >
            <Icon.Search className="me-2" />
            Browse Tests
          </Button>
        </div>

        {/* Player format selector */}
        <Form.Label>Player Format</Form.Label>
        <Form.Select
          value={formData.config?.['player-format'] || "flashcard-1"}
          onChange={(e) => handleChange("config", {
            ...formData.config,
            'player-format': e.target.value
          })}
        >
          <option value="flashcard-1">Flashcard Format</option>
          <option value="standard">Standard Quiz</option>
        </Form.Select>
        <Form.Text className="text-muted">
          Choose how the test will be displayed to students
        </Form.Text>

        {/* Test Browser Modal */}
        <TestBrowserModal
          show={showTestBrowser}
          onHide={() => setShowTestBrowser(false)}
          onSelectTest={(test) => {
            setSelectedTest(test);
            handleChange("config", {
              ...formData.config,
              testId: test.id
            });
            setShowTestBrowser(false);
          }}
          selectedTestId={formData.config?.testId}
        />
      </div>
    );
  };

  const renderContentInput = () => {
    return (
      <Form.Group className="mb-3">
        <Form.Label>Article Content <span className="text-danger">*</span></Form.Label>
        <CKEditor
          editor={ClassicEditor}
          data={formData.content || ""}
          onChange={(_event: any, editor: any) => {
            handleChange("content", editor.getData());
          }}
          config={{
            extraPlugins: [imageUploadPlugin],
            placeholder: "Enter your article content here...",
          }}
        />
        <Form.Text className="text-muted">
          Use the editor toolbar for formatting and inserting images
        </Form.Text>
      </Form.Group>
    );
  };

  const renderInteractivePathwayInput = () => (
    <DecisionResourceStudio
      content={formData.content || ""}
      onContentChange={(c) => handleChange("content", c)}
      isNew={!resource}
      hasName={!!formData.name}
      onSuggestName={(name) => handleChange("name", name)}
    />
  );

  // Mark as Important is only relevant for Article resources whose subtype is "Announcement"
  const selectedSubtype = getSubtypesForSelectedType().find((st) => st.id === formData.subTypeId);
  const showMarkAsImportant =
    formData.typeId === STUDY_RESOURCE_TYPE.ARTICLE &&
    !!selectedSubtype?.name?.toLowerCase().includes("announce");

  return (
    <Modal
      show={show}
      onHide={onHide}
      backdrop={true}
      dialogClassName="sr-form-modal"
      className="study-resource-form-drawer"
      centered
    >
      <Modal.Header closeButton>
        <Modal.Title>
          <div className="d-flex align-items-center gap-2">
            <Icon.Edit2 size={18} />
            {resource ? "Edit" : "Create"} Study Resource
          </div>
          {(() => {
            const typeName = resourceTypes.find((t) => t.id === formData.typeId)?.name;
            const idSuffix = resource?.id ? ` · #${resource.id}` : "";
            return typeName ? (
              <span className="sr-subtitle">{typeName}{idSuffix}</span>
            ) : null;
          })()}
        </Modal.Title>
      </Modal.Header>

      <Form onSubmit={handleSubmit} className="d-flex flex-column sr-form-modal-form">
        <Modal.Body className="flex-grow-1 overflow-auto">
          {/* ─── Section 1: Basics ─── */}
          <div className="sr-section">
            <div className="sr-section-title">
              <Icon.FileText size={13} />
              Basics
            </div>

            <Form.Group className="mb-3">
              <Form.Label>
                Resource Name
                <span className="sr-required-pill">Required</span>
              </Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter resource name"
                value={formData.name}
                onChange={(e) => handleChange("name", e.target.value)}
                required
              />
            </Form.Group>

            <Form.Group className={getSubtypesForSelectedType().length > 0 ? "mb-3" : "mb-0"}>
              <Form.Label>
                Resource Type
                <span className="sr-required-pill">Required</span>
              </Form.Label>
              <Form.Select
                value={formData.typeId}
                onChange={(e) => handleTypeChange(Number(e.target.value))}
                required
                disabled={resourceTypesLoading}
              >
                {resourceTypesLoading ? (
                  <option>Loading types...</option>
                ) : resourceTypes.length > 0 ? (
                  resourceTypes
                    .filter(type => type.enableStatus === 1)
                    .slice()
                    .sort((a, b) => a.name.localeCompare(b.name))
                    .map(type => (
                      <option key={type.id} value={type.id}>
                        {type.name}
                      </option>
                    ))
                ) : (
                  <>
                    <option value={STUDY_RESOURCE_TYPE.DOWNLOAD}>Download</option>
                    <option value={STUDY_RESOURCE_TYPE.REFERENCE}>Reference</option>
                    <option value={STUDY_RESOURCE_TYPE.ARTICLE}>Article</option>
                    <option value={STUDY_RESOURCE_TYPE.EBOOK}>E-Book</option>
                    <option value={STUDY_RESOURCE_TYPE.VIMEO}>Vimeo Video</option>
                    <option value={STUDY_RESOURCE_TYPE.YOUTUBE}>YouTube Video</option>
                    <option value={STUDY_RESOURCE_TYPE.ONLINE_TEST}>Online Test</option>
                    <option value={STUDY_RESOURCE_TYPE.CODE_EDITOR}>Code Editor (Judge0)</option>
                  </>
                )}
              </Form.Select>
            </Form.Group>

            {getSubtypesForSelectedType().length > 0 && (
              <Form.Group className="mb-0">
                <Form.Label>
                  Sub-Type
                  <span className="sr-optional-pill">Optional</span>
                </Form.Label>
                <Form.Select
                  value={formData.subTypeId || ""}
                  onChange={(e) => handleChange("subTypeId", e.target.value ? Number(e.target.value) : null)}
                >
                  <option value="">Select a sub-type</option>
                  {getSubtypesForSelectedType().map(subtype => (
                    <option key={subtype.id} value={subtype.id}>
                      {subtype.name}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            )}
          </div>

          {/* ─── Section 2: Grading & Visibility ─── */}
          <div className="sr-section">
            <div className="sr-section-title">
              <Icon.CheckCircle size={13} />
              Grading & Visibility
            </div>

            <Form.Group className="mb-3">
              <Form.Label>
                Grading Rubric
                <span className="sr-optional-pill">Optional</span>
              </Form.Label>
              <InputGroup>
                <Form.Select
                  value={formData.rubricId || ""}
                  onChange={(e) => handleChange("rubricId", e.target.value ? Number(e.target.value) : null)}
                  disabled={rubricsLoading}
                >
                  <option value="">No rubric (manual grading)</option>
                  {rubrics.map((rubric) => (
                    <option key={rubric.id} value={rubric.id}>
                      {rubric.name} (Max: {rubric.maxScore} pts)
                    </option>
                  ))}
                </Form.Select>
                <Button
                  variant="secondary"
                  href="/rubrics"
                  target="_blank"
                  title="Manage rubrics"
                >
                  <Icon.Settings size={16} />
                </Button>
              </InputGroup>
              {(() => {
                const r = rubrics.find((x) => x.id === formData.rubricId);
                return r ? (
                  <div className="sr-rubric-chip">
                    <Icon.CheckCircle size={14} />
                    <span>{r.name}</span>
                    <span className="sr-rubric-chip-meta">Max {r.maxScore} pts</span>
                  </div>
                ) : (
                  <Form.Text className="text-muted">
                    Assign a rubric for AI-powered grading and evaluation criteria
                  </Form.Text>
                );
              })()}
            </Form.Group>

            {/* Mark as Important applies to Article resources with the Announcement subtype only */}
            {showMarkAsImportant && (
              <div className="sr-toggle-row" style={{ paddingBottom: 0 }}>
                <div className="sr-toggle-meta">
                  <div className="sr-toggle-title">
                    <Icon.Star
                      size={14}
                      style={{
                        color: markAsImportant ? '#f59e0b' : '#94a3b8',
                        fill: markAsImportant ? '#f59e0b' : 'none',
                      }}
                    />
                    Mark as Important
                  </div>
                  <div className="sr-toggle-help">
                    Pin this resource to the student dashboard ticker
                  </div>
                </div>
                <label className="sr-switch">
                  <input
                    type="checkbox"
                    checked={markAsImportant}
                    onChange={(e) => setMarkAsImportant(e.target.checked)}
                  />
                  <span className="sr-switch-slider" />
                </label>
              </div>
            )}
          </div>

          {/* ─── Section 3: Type-Specific Content ─── */}
          <div className="sr-section" style={{ marginBottom: '70px' }}>
            <div className="sr-section-title">
              <Icon.Layers size={13} />
              {formData.typeId === SCORM_COURSE_TYPE_ID ? 'SCORM Package' :
               formData.typeId === STUDY_RESOURCE_TYPE.INTERACTIVE_PATHWAY ? 'Pathway Content' :
               formData.typeId === STUDY_RESOURCE_TYPE.SPIN_WHEEL ? 'Spin Wheel Setup' :
               formData.typeId === STUDY_RESOURCE_TYPE.ARTICLE ? 'Article Content' :
               formData.typeId === STUDY_RESOURCE_TYPE.VIMEO ? 'Video Source' :
               formData.typeId === STUDY_RESOURCE_TYPE.ONLINE_TEST ? 'Test Selection' :
               formData.typeId === STUDY_RESOURCE_TYPE.CODE_EDITOR ? 'Code Editor' :
               formData.typeId === STUDY_RESOURCE_TYPE.DISCUSSION_BOARD ? 'Discussion Board Settings' /* [discussion-board type=20] */ :
               (formData.typeId === STUDY_RESOURCE_TYPE.DOWNLOAD || formData.typeId === STUDY_RESOURCE_TYPE.ARTICLE || formData.typeId === STUDY_RESOURCE_TYPE.EBOOK) ? 'File Upload' :
               'Resource URL'}
            </div>
            {formData.typeId === SCORM_COURSE_TYPE_ID ? (
               <div className="sr-scorm-pointer">
                 <p style={{ marginBottom: 10, color: 'var(--text-body)', fontSize: 14 }}>
                   SCORM courses are not created here — they are uploaded through the{' '}
                   <strong>SCORM Course Library</strong>, which imports the package into SCORM Cloud.
                   Once imported, the course appears in this resource list automatically and can be
                   issued or attached to learning modules like any other resource.
                 </p>
                 <Button variant="primary" href="/scorm-courses" target="_blank">
                   Open SCORM Course Library
                 </Button>
               </div>
             ) :
             formData.typeId === STUDY_RESOURCE_TYPE.VIMEO ? renderVimeoInput() :
             formData.typeId === STUDY_RESOURCE_TYPE.ONLINE_TEST ? renderTestInput() :
             formData.typeId === STUDY_RESOURCE_TYPE.INTERACTIVE_PATHWAY ? renderInteractivePathwayInput() :
             formData.typeId === STUDY_RESOURCE_TYPE.SPIN_WHEEL ? (
               <SpinWheelEditor content={formData.content} onChange={(json) => handleChange("content", json)} />
             ) :
             /* [discussion-board type=20] render the six board fields; URL/Upload are not applicable */
             formData.typeId === STUDY_RESOURCE_TYPE.DISCUSSION_BOARD ? renderDiscussionBoardInput() :
             formData.typeId === STUDY_RESOURCE_TYPE.CODE_EDITOR ? (
               <Form.Group className="mb-0">
                 <Form.Label>Code Editor URL</Form.Label>
                 <Form.Control
                   type="text"
                   value="judge0://code-editor"
                   disabled
                 />
                 <Form.Text className="text-muted">
                   This resource opens an interactive code editor powered by Judge0
                 </Form.Text>
               </Form.Group>
             ) :
             (formData.typeId === STUDY_RESOURCE_TYPE.DOWNLOAD || formData.typeId === STUDY_RESOURCE_TYPE.ARTICLE || formData.typeId === STUDY_RESOURCE_TYPE.EBOOK) ? (
               <>
                 {renderDownloadInput()}
                 {formData.typeId === STUDY_RESOURCE_TYPE.ARTICLE && renderContentInput()}
               </>
             ) :
             renderUrlInput()}
          </div>
        </Modal.Body>

        <div style={{ position: 'sticky', bottom: 0, zIndex: 2 }}>
          {error && (
            <Alert
              variant="danger"
              dismissible
              onClose={() => setError(null)}
              className="mb-0 rounded-0 border-start-0 border-end-0"
            >
              {error}
            </Alert>
          )}
          <div className="border-top p-3 bg-light d-flex gap-2 justify-content-end">
            <Button variant="secondary" onClick={onHide} disabled={loading || isUploading}>
              Cancel
            </Button>
            <Button variant="primary" type="submit" disabled={loading || isUploading || formData.typeId === SCORM_COURSE_TYPE_ID}>
              {loading ? (
                <>
                  <Spinner role="status" animation="border" size="sm" className="me-2" />
                  {resource ? "Updating..." : "Creating..."}
                </>
              ) : (
                <>
                  <Icon.Save className="me-2" />
                  {resource ? "Update" : "Create"} Resource
                </>
              )}
            </Button>
          </div>
        </div>
      </Form>
    </Modal>
  );
};

export default StudyResourceFormModal;
